import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { db, auth } from "../firebaseConfig";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth"; // 🔥 IMPORTANT IMPORT

// Image Import
import companyLogo from "./icon.png"; 

function Sidebar({ onLogout, notificationCount }) {
  const [search, setSearch] = useState("");

  // 🔥 Loading State (Taaki jab tak data na aaye, Sidebar wait kare)
  const [loading, setLoading] = useState(true);

  // States
  const [userRole, setUserRole] = useState("Service Engineer"); 
  const [permissions, setPermissions] = useState({});
  const location = useLocation(); 

  // =========================================================
  // 1. FETCH USER ROLE (FIXED FOR LIVE SERVER)
  // =========================================================
  useEffect(() => {
      // 🔥 Listen for Auth Changes (Wait for User)
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (user) {
              const emailKey = user.email.toLowerCase();

              // Admin Bypass
              if (emailKey === "varunkhandagre@gmail.com" || emailKey === "admin@lms.com") {
                  setUserRole("Admin"); 
              } else {
                  // DB Check
                  try {
                      const docRef = doc(db, "users", emailKey);
                      const docSnap = await getDoc(docRef);
                      
                      if (docSnap.exists()) {
                          const dbRole = docSnap.data().role;
                          let finalRole = dbRole || "Service Engineer";

                          // MAPPING LOGIC
                          const r = finalRole.toLowerCase();
                          if (r.includes('engineer') || r.includes('service')) finalRole = 'Service Engineer';
                          else if (r.includes('sales')) finalRole = 'Sales Executive';
                          else if (r.includes('account')) finalRole = 'Accountant';
                          else if (r.includes('store') || r.includes('back') || r.includes('office')) finalRole = 'Store Keeper';

                          setUserRole(finalRole);
                      } else {
                          setUserRole("Service Engineer"); 
                      }
                  } catch (error) {
                      console.error("Role Fetch Error:", error);
                      setUserRole("Service Engineer");
                  }
              }
          }
          // 🔥 Stop Loading when check is done
          setLoading(false);
      });

      return () => unsubscribe();
  }, []);

  // =========================================================
  // 2. FETCH PERMISSIONS
  // =========================================================
  useEffect(() => {
      const unsub = onSnapshot(doc(db, "settings", "permissions"), (doc) => {
          if(doc.exists()) {
             setPermissions(doc.data());
          }
      });
      return () => unsub();
  }, []);

  // Permission Checker
  const canSee = (key) => {
      if(userRole === 'Admin') return true; 
      if(!key) return true; 
      
      const myPerms = permissions[userRole] || {};
      return myPerms[key] === true; 
  };

  // 🔥 LOADING SCREEN (Taaki khali sidebar na dikhe)
  if (loading) {
      return (
        <div style={{...sidebarStyle, justifyContent:"center", alignItems:"center"}}>
            <p style={{color:"#bdc3c7"}}>Loading Menu...</p>
        </div>
      );
  }

  // 🔥 MENU ITEMS (Updated)
  const menuItems = [
    { 
        // 🔥 NEW SECTION: CALENDAR IS HERE
        title: "DASHBOARD & BASICS", 
        items: [
            { name: "Calendar Planner", path: "/calendar", icon: "📅", key: "calendar" },
        ]
    },
    { 
        title: "ACCOUNTS & FINANCE", 
        items: [
            { name: "Manage Dues", path: "/payment-dues", icon: "⏳", key: "payment_due" },
            { name: "Collections", path: "/payment-collections", icon: "💰", key: "payment_coll" },
            { name: "Expenses", path: "/expenses", icon: "💸", key: "expenses" },
            { name: "Advance Req", path: "/advances", icon: "💵", key: "advance" },
        ]
    },
    { 
        title: "HR & TEAM", 
        items: [
            { name: "Attendance", path: "/attendance", icon: "📅", key: "attendance" },
            { name: "Leave Requests", path: "/leaves", icon: "📝", key: "leave" },
            { name: "Task Manager", path: "/tasks", icon: "✅", key: "tasks" },
            { name: "Timeline View", path: "/timeline", icon: "🕵️‍♂️", key: "timeline" },
            { name: "Travel Logs", path: "/travel", icon: "🚲", key: "travel" },
            { name: "Card Requests", path: "/card-requests", icon: "📇", key: "card_req" },
        ]
    },
    { 
        title: "SALES & LEADS", 
        items: [
            { name: "Leads Master", path: "/leads", icon: "👥", key: "leads" },
            { name: "Orders (PO)", path: "/orders", icon: "🛒", key: "orders" },
            { name: "Daily Visits", path: "/reports", icon: "📊", key: "visits" },
            { name: "Activity Plan", path: "/activity", icon: "📅", key: "activity" },
            { name: "Product Demos", path: "/demos", icon: "🎭", key: "demos" },
            { name: "PDF Catalogs", path: "/catalogs", icon: "📚", key: "catalogs" },
            { name: "Sales Analysis", path: "/sales_analysis", icon: "📈", key: "sales_analysis" }, 
        ]
    },
    { 
        title: "SERVICE & SUPPORT", 
        items: [
            { name: "Service Tickets", path: "/service-calls", icon: "📞", key: "tickets" },
            { name: "Service Reports", path: "/service-reports", icon: "🛠️", key: "service_reports" }, 
            { name: "PMS Schedule", path: "/pms", icon: "🔧", key: "pms" },
            { name: "AMC / Contracts", path: "/amc", icon: "📜", key: "amc_cmc" }, // 🔥 ADDED HERE
            { name: "Installations", path: "/installations", icon: "⚙️", key: "installation" },
            { name: "Organizations", path: "/organizations", icon: "🏥", key: "organizations" },
            { name: "Spare Parts", path: "/spare-parts", icon: "🔩", key: "spares" },
            { name: "Courier / Dispatch", path: "/couriers", icon: "📦", key: "courier" },             
        ]
    },
    { 
        title: "360 View", 
        items: [
            { name: "Live Map", path: "/map-view", icon: "🗺️", key: "map_view" },
            { name: "Machine Kundali", path: "/asset-history", icon: "🔍", key: "asset_history" },
            { name: "Hospital Kundali", path: "/org-history", icon: "🏥", key: "org_history" },
            { name: "Employee Kundali", path: "/emp-history", icon: "🕵️‍♂️", key: "emp_history" },
        ]
    },
    { 
        title: "Settings", 
        items: [
            { name: "Settings", path: "/settings", icon: "⚙️", key: "settings" },
            { name: "Manage Users", path: "/users", icon: "👥", key: "users" },
            { name: "Access Control", path: "/permissions", icon: "🛡️", key: "access_control" },
            { name: "My Profile", path: "/profile", icon: "👤" },
            
        ]
    }
  ];

  return (
    <div style={sidebarStyle}>
      {/* HEADER */}
      <div style={{textAlign: "center", marginBottom: "20px"}}>
        
        {/* LOGO */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", borderBottom: "1px solid #34495e", paddingBottom: "10px", marginBottom: "15px" }}>
             <img src={companyLogo} alt="Logo" style={{ width: "40px", height: "auto" }} />
             <h2 style={{ color: "white", margin: 0, fontSize: "20px" }}>LMS_CRM</h2>
        </div>
        
        {/* Role Display */}
        <div style={{color:"#f39c12", fontSize:"10px", fontWeight:"bold", marginBottom:"10px", textTransform:"uppercase"}}>
            {userRole} Mode
        </div>

        <input 
            type="text" 
            placeholder="🔎 Search..." 
            value={search} 
            onChange={(e) => setSearch(e.target.value)} 
            style={searchStyle} 
        />
      </div>

      {/* DASHBOARD BUTTON */}
      <Link to="/" style={{
          ...linkStyle, 
          backgroundColor: location.pathname === '/' ? "#27ae60" : "rgba(255,255,255,0.1)", // Active Color
          justifyContent:"space-between"
      }}>
        <span style={{display:"flex", alignItems:"center"}}>
            <span style={{marginRight:"10px"}}>🏠</span> Dashboard
        </span>
        {notificationCount > 0 && (
            <span style={badgeStyle}>{notificationCount}</span>
        )}
      </Link>

      {/* MENU ITEMS */}
      <div style={scrollAreaStyle}>
        {menuItems.map((section, index) => {
            const filteredItems = section.items.filter(item => {
                const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());
                const isAllowed = canSee(item.key);
                return matchesSearch && isAllowed;
            });
            
            if (filteredItems.length === 0) return null;
            
            return (
                <div key={index} style={{marginBottom: "20px"}}>
                    <div style={sectionTitle}>{section.title}</div>
                    {filteredItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        
                        return (
                            <Link 
                                key={item.path} 
                                to={item.path} 
                                style={{
                                    ...linkStyle, 
                                    backgroundColor: isActive ? "#34495e" : "transparent",
                                    borderLeft: isActive ? "4px solid #3498db" : "4px solid transparent",
                                    fontWeight: isActive ? "bold" : "normal"
                                }}
                            >
                                <span style={{marginRight:"12px", width:"20px", textAlign:"center"}}>{item.icon}</span>
                                {item.name}
                            </Link>
                        );
                    })}
                </div>
            );
        })}
      </div>

      {/* FOOTER */}
      <div style={footerStyle}>
        <button onClick={onLogout} style={logoutBtnStyle}>🚪 Logout</button>
      </div>
    </div>
  );
}

// STYLES
const sidebarStyle = { width: "250px", minWidth: "250px", backgroundColor: "#2c3e50", color: "#ecf0f1", display: "flex", flexDirection: "column", height: "100vh", padding: "15px", boxSizing: "border-box", borderRight: "1px solid #1a252f" };
const searchStyle = { width: "100%", padding: "8px 10px", borderRadius: "4px", border: "none", backgroundColor: "#34495e", color: "white", outline: "none", boxSizing: "border-box" };
const scrollAreaStyle = { flex: 1, overflowY: "auto", marginTop: "10px", paddingRight: "5px" };
const sectionTitle = { fontSize: "11px", fontWeight: "bold", color: "#95a5a6", marginBottom: "5px", marginTop: "10px", paddingLeft: "10px", textTransform: "uppercase", letterSpacing: "0.5px" };
const linkStyle = { display: "flex", alignItems: "center", padding: "10px 10px", color: "#ecf0f1", textDecoration: "none", fontSize: "14px", borderRadius: "4px", marginBottom: "2px", transition: "all 0.2s" };
const badgeStyle = { backgroundColor: "#e74c3c", color: "white", padding: "2px 6px", borderRadius: "10px", fontSize: "10px", fontWeight: "bold" };
const footerStyle = { marginTop: "auto", paddingTop: "15px", borderTop: "1px solid #34495e" };
const logoutBtnStyle = { width: "100%", padding: "10px", backgroundColor: "#c0392b", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "bold" };

export default Sidebar;